// Shared helpers for creator clip submissions (admin board, reviewer queue, review modal)

import type { Reviewer, SubmissionVerdict, SubmittedClip, SubmittedClipReview } from '@/types';

// reviews[] plus the legacy single `review` when it isn't already in the list
export function getSubmissionReviews(submission: SubmittedClip): SubmittedClipReview[] {
  const reviews = [...(submission.reviews || [])];
  if (submission.review && !reviews.some(r => r.reviewerId === submission.review!.reviewerId)) {
    reviews.push(submission.review);
  }
  return reviews;
}

export const VERDICTS: Record<SubmissionVerdict, { label: string; short: string; chip: string }> = {
  approve: { label: 'Approve', short: 'Approve', chip: 'bg-lime-500/15 text-lime-300 border-lime-500/30' },
  revise: { label: 'Needs revisions', short: 'Revise', chip: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  reject: { label: 'Reject', short: 'Reject', chip: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

export const STATUS_CHIPS: Record<SubmittedClip['status'], string> = {
  Submitted: 'bg-zinc-700/60 text-zinc-300 border-zinc-600',
  'Under Review': 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  Approved: 'bg-lime-500/15 text-lime-300 border-lime-500/30',
  'Needs Revision': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  Rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
};

// Statuses only the admin sets; anything else is still waiting for a decision
export const DECIDED_STATUSES: SubmittedClip['status'][] = ['Approved', 'Needs Revision', 'Rejected'];
export const isDecided = (submission: SubmittedClip) => DECIDED_STATUSES.includes(submission.status);

export function verdictTally(reviews: SubmittedClipReview[]): Record<SubmissionVerdict | 'none', number> {
  const tally = { approve: 0, revise: 0, reject: 0, none: 0 };
  for (const r of reviews) tally[r.verdict ?? 'none']++;
  return tally;
}

export function averageQuality(reviews: SubmittedClipReview[]): number | null {
  const rated = reviews.filter(r => r.qualityRating > 0);
  if (rated.length === 0) return null;
  return Math.round((rated.reduce((sum, r) => sum + r.qualityRating, 0) / rated.length) * 10) / 10;
}

// Compliance checks a reviewer left unticked
export function complianceFlags(review: SubmittedClipReview): string[] {
  const flags: string[] = [];
  if (!review.onBrand) flags.push('Off brand');
  if (!review.showsProduct) flags.push('Product not shown');
  if (!review.claimsCompliant) flags.push('Claims issue');
  return flags;
}

export const genderLabel = (gender?: SubmittedClip['gender']) =>
  gender === 'male' ? 'Male' : gender === 'female' ? 'Female' : gender === 'couple' ? 'Couple' : undefined;

// Reviews are saved under either reviewers.id or reviewers.code
export function reviewerLabel(reviewers: Reviewer[], reviewerId: string): string {
  const reviewer = reviewers.find(r => r.id === reviewerId || r.code === reviewerId);
  if (!reviewer) return reviewerId;
  return reviewer.name ? `${reviewer.name} · ${reviewer.code}` : reviewer.code;
}

// Categories reviewers suggested most often, best first
export function suggestedCategoryIds(reviews: SubmittedClipReview[]): string[] {
  const counts = new Map<string, number>();
  for (const r of reviews) for (const id of r.suggestedCategories || []) counts.set(id, (counts.get(id) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
}

export function ageLabel(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
